import { LinkSize } from '@vizit/atoms';
import { BookSharpIcon, ChevronRightIcon, CompassIcon, Home01Icon, type IconProps } from '@vizit/icons';
import { cva, cx } from '@vizit/theme';
import type { ComponentType } from 'react';
import { createElement, isValidElement } from 'react';
import { Breadcrumbs as AriaBreadcrumbs, Breadcrumb } from 'react-aria-components';
import { RouterLink } from '../router-link';
import type { BreadcrumbsProps } from './types';

/**
 * Maps breadcrumb hrefs to their corresponding icon components
 * Icons are added at render time to avoid serialization issues with browser history
 */
const BREADCRUMB_ICON_MAP: Record<string, ComponentType<IconProps>> = {
  '/': Home01Icon,
  '/library': BookSharpIcon,
  '/explore': CompassIcon,
};

const getIconSize = (linkSize: LinkSize): number => {
  switch (linkSize) {
    case LinkSize.Sm:
      return 16;
    case LinkSize.Md:
      return 20;
    case LinkSize.Lg:
      return 24;
    default:
      return 16;
  }
};

const breadcrumbLinkClasses = cva({
  base: [
    'text-gray-utility-500 font-semibold overflow-hidden text-ellipsis whitespace-nowrap no-underline py-2 px-4 outline-none',
    'focus-visible:ring-2 focus-visible:ring-gray-modern-500 focus-visible:text-gray-utility-700',
  ],
  variants: {
    isCurrent: {
      true: 'bg-primary-hover cursor-default',
      false: 'hover:bg-primary-hover hover:text-gray-utility-700 hover:underline',
    },
    isDisabled: {
      true: 'cursor-not-allowed text-disabled bg-disabled-subtle',
      false: '',
    },
  },
  defaultVariants: {
    isCurrent: false,
    isDisabled: false,
  },
});

export function Breadcrumbs(props: BreadcrumbsProps) {
  const { items, maxLabelWidth = 280, className, size = LinkSize.Sm, ...rest } = props;

  if (items.length < 2) return null;

  return (
    <AriaBreadcrumbs className={cx('flex items-center gap-1', className)} {...rest}>
      {items.map((item, index) => {
        const isLast = index === items.length - 1;
        const isCurrent = isLast && !item.href;
        const iconSize = getIconSize(size);

        // Map icon from href if not already present
        // This ensures icons are only added at render time, never stored in navigation state
        const icon = item.icon || (item.href ? BREADCRUMB_ICON_MAP[item.href] : undefined);

        const renderContent = () => {
          if (!icon) return item.label;

          const iconElement = isValidElement(icon)
            ? icon
            : createElement(icon as React.ComponentType<IconProps>, {
                size: iconSize,
              });

          return (
            <span className="flex items-center gap-4">
              {iconElement}
              <span>{item.label}</span>
            </span>
          );
        };

        return (
          <Breadcrumb
            // biome-ignore lint/suspicious/noArrayIndexKey: this is fine
            key={index}
            className="flex items-center gap-2 text-gray-utility-500"
          >
            {isCurrent || !item.href ? (
              <span
                aria-current={isCurrent ? 'page' : undefined}
                className={cx(
                  breadcrumbLinkClasses({ isCurrent, isDisabled: !!item.isDisabled }),
                  'cursor-default',
                  size === LinkSize.Sm ? 'text-sm' : size === LinkSize.Md ? 'text-base' : 'text-lg',
                )}
                style={{ maxWidth: maxLabelWidth }}
              >
                {renderContent()}
              </span>
            ) : (
              <RouterLink
                aria-label={item.label}
                size={size}
                to={item.href}
                search={item.search}
                className={breadcrumbLinkClasses({ isCurrent: false, isDisabled: !!item.isDisabled })}
                isDisabled={!!item.isDisabled}
                style={{ maxWidth: maxLabelWidth }}
              >
                {renderContent()}
              </RouterLink>
            )}
            {!isLast && <ChevronRightIcon size={iconSize} />}
          </Breadcrumb>
        );
      })}
    </AriaBreadcrumbs>
  );
}
